import { type FormInstance, Form, Select } from 'antd5';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllDamageSchemasQuery } from 'types/atlas-graphql';

type DamageEditSchemaProps = {
  form: FormInstance;
  schemaId: string;
  onChange: (schemaId: string) => void;
  externalLoading: boolean;
};

export default function DamageEditSchema({
  form,
  schemaId,
  onChange,
  externalLoading,
}: DamageEditSchemaProps) {
  const { t } = useTranslation();
  const { data, loading } = useAllDamageSchemasQuery();
  const damageSchemas = useMemo(() => data?.damageSchemas ?? [], [data]);

  useEffect(() => {
    const _schemaId =
      schemaId ?? damageSchemas.find(({ isDefault }) => isDefault)?.id ?? damageSchemas[0]?.id;
    if (_schemaId) {
      form.setFieldsValue({ schemaId: _schemaId });
      onChange(_schemaId);
    }
  }, [form, schemaId, onChange, damageSchemas]);

  return (
    <Form.Item
      label={t('damage.damageSchema', 'Damage Schema')}
      name="schemaId"
      style={damageSchemas?.length < 2 ? { visibility: 'hidden', height: 0, marginBottom: 0 } : {}}
      rules={[
        { required: true, message: t('damage.selectSchemaRequired', 'Please select a Schema') },
      ]}
    >
      <Select
        className="testid-damage-schema-edit"
        style={{ width: '100%' }}
        allowClear={false}
        onChange={onChange}
        getPopupContainer={triggerNode => triggerNode.parentElement}
        loading={loading || externalLoading}
      >
        {damageSchemas.map(({ id, name, isDefault }) => (
          <Select.Option key={id} value={id}>
            {isDefault ? t('damage.schemaDefaultLabel', '{{name}} (default)', { name }) : name}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
}
